import { TableComponentProps } from '@/types/tableComponent.types';
import styles from './tableComponent.module.scss';

const isSummaryRow = (cells: string[]) =>
  cells[0] === '' && cells.some((c) => c.toLowerCase().includes('total'));

type RowGroup = {
  mainRow: { id: number; cells: string[] };
  subRows: { id: number; cells: string[] }[];
};

const groupRows = (rows: { id: number; cells: string[] }[]): RowGroup[] => {
  const groups: RowGroup[] = [];
  let current: RowGroup | null = null;
  for (const row of rows) {
    if (row.cells[0] !== '') {
      current = { mainRow: row, subRows: [] };
      groups.push(current);
    } else if (current) {
      current.subRows.push(row);
    }
  }
  return groups;
};

const TableComponent = ({ title, description, columns, rows }: TableComponentProps) => {
  const groups = groupRows(rows);

  return (
    <section className="container">
      <div className={styles.tableComponent}>
        {title && <h2 className={styles.title}>{title}</h2>}
        {description && <p className={styles.description}>{description}</p>}

        {/* Desktop table */}
        <div className={`${styles.tableWrapper} ${styles.desktopOnly}`}>
          <table className={styles.table}>
            <thead>
              <tr>
                {columns.map((col, i) => (
                  <th key={i} className={styles.th}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const summary = isSummaryRow(row.cells);
                return (
                  <tr
                    key={row.id}
                    className={`${styles.tr} ${summary ? styles.trSummary : ''}`}
                  >
                    {row.cells.map((cell, i) => (
                      <td
                        key={i}
                        className={`${styles.td} ${summary && cell ? styles.tdSummary : ''}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className={`${styles.cards} ${styles.mobileOnly}`}>
          {groups.map((group) => (
            <div key={group.mainRow.id} className={styles.card}>
              <div className={styles.cardHeader}>
                {group.mainRow.cells[0]}
              </div>
              <div className={styles.cardBody}>
                {columns.slice(1).map((col, i) => (
                  <div key={i} className={styles.cardRow}>
                    <span className={styles.cardLabel}>{col}</span>
                    <span className={styles.cardValue}>
                      {group.mainRow.cells[i + 1]}
                    </span>
                  </div>
                ))}
                {group.subRows.map((sub) =>
                  isSummaryRow(sub.cells) ? (
                    <div key={sub.id} className={`${styles.cardRow} ${styles.cardSummary}`}>
                      <span className={styles.cardLabel}>
                        {sub.cells.find((c) => c.toLowerCase().includes('total'))}
                      </span>
                      <span className={styles.cardValue}>
                        {sub.cells[sub.cells.findIndex((c) => c.toLowerCase().includes('total')) + 1]}
                      </span>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TableComponent;
